import { Typography } from "@mui/material";
import { useMemo, useState } from "react";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { ControlSeverityMssp } from "../../../../utils/constants";
import ImportanceCell from "../ImportanceCell";
import CoverageScoreLabel from "./CoverageScoreLabel";
import { getNumberOfGapsBySeverity, getTotalGapsBySeverity } from "./utils";

const StackCoverageTable = ({ rows, activeSeverity, isLoadingTenantsData }) => {
  const [sortModel, setSortModel] = useState([
    {
      field: "importance",
      sort: "desc",
    },
    {
      field: "total_gaps",
      sort: "desc",
    },
  ]);

  const columns = useMemo(
    () => [
      {
        field: "tenant_name",
        headerName: "Tenant",
        flex: 1,
        sortable: false,

        renderHeader: () => (
          <Typography
            variant="subtitle2"
            sx={{
              padding: 1,
            }}
          >
            Tenant
          </Typography>
        ),
        renderCell: (params) => (
          <Typography variant="caption" sx={{ padding: 1 }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "open_gaps",
        headerName: "Open Gaps",
        flex: 1,
        sortable: false,
        renderHeader: () => (
          <Typography variant="subtitle2">Open Gaps</Typography>
        ),
        renderCell: (params) => {
          const numOfGaps = getNumberOfGapsBySeverity(
            params.row,
            activeSeverity
          );

          return (
            <Typography variant="caption" sx={{ padding: 1 }}>
              {numOfGaps}
            </Typography>
          );
        },
      },
      {
        field: "compliant",
        headerName: "Compliant",
        flex: 1,
        sortable: false,
        renderHeader: () => (
          <Typography variant="subtitle2">
            {activeSeverity === ControlSeverityMssp.COMPLIANT
              ? "Total compliant controls"
              : "Compliant"}
          </Typography>
        ),
        renderCell: (params) => {
          const row = params.row;
          const numOfGaps = getNumberOfGapsBySeverity(row, activeSeverity);
          const totalSeverityControls = getTotalGapsBySeverity(
            row,
            activeSeverity
          );

          return (
            <Typography variant="caption" sx={{ padding: 1 }}>
              {totalSeverityControls - numOfGaps}
            </Typography>
          );
        },
      },
      {
        field: "total_controls",
        headerName: "Total",
        flex: 1,
        sortable: false,
        renderHeader: () => <Typography variant="subtitle2">Total</Typography>,
        renderCell: (params) => {
          const row = params.row;
          const totalSeverityControls =
            activeSeverity === ControlSeverityMssp.NOT_DEPLOYED
              ? row?.not_deployed
              : getTotalGapsBySeverity(row, activeSeverity);

          return (
            <Typography variant="caption" sx={{ padding: 1 }}>
              {totalSeverityControls}
            </Typography>
          );
        },
      },

      {
        field: "coverage_score",
        headerName: "Coverage Score",
        flex: 1,
        sortable: false,
        renderHeader: () => (
          <Typography variant="subtitle2">Coverage Score</Typography>
        ),
        renderCell: (params) => {
          const row = params.row;
          const numOfGaps = getNumberOfGapsBySeverity(row, activeSeverity);
          const totalGaps = getTotalGapsBySeverity(row, activeSeverity);

          if (totalGaps === 0) {
            return <CoverageScoreLabel value={100} />;
          }
          const coverageScore = 100 - (numOfGaps / totalGaps) * 100;
          return <CoverageScoreLabel value={coverageScore.toFixed(0)} />;
        },
      },
      {
        field: "importance",
        headerName: "Importance",
        flex: 1,
        sortable: true,
        renderHeader: () => (
          <Typography variant="subtitle2">Importance</Typography>
        ),
        renderCell: (params) => {
          return <ImportanceCell value={params.value} row={params.row} />;
        },
      },
    ],
    [activeSeverity]
  );

  const TotalCompliantColumn = {
    field: "total_compliant",
    headerName: "Total compliant controls",
    flex: 1,
    sortable: false,
    renderHeader: () => (
      <Typography variant="subtitle2">Total compliant controls</Typography>
    ),
    renderCell: (params) => {
      return (
        <Typography variant="caption" sx={{ padding: 1 }}>
          {params.row[ControlSeverityMssp.COMPLIANT]}
        </Typography>
      );
    },
  };

  const filterColumns = useMemo(() => {
    if (
      activeSeverity === ControlSeverityMssp.NOT_DEPLOYED ||
      activeSeverity === ControlSeverityMssp.COMPLIANT
    ) {
      let filteredColumns = columns.filter(
        (column) =>
          column.field !== "open_gaps" &&
          column.field !== "coverage_score" &&
          column.field !== "compliant"
      );
      if (activeSeverity === ControlSeverityMssp.COMPLIANT) {
        filteredColumns = filteredColumns.filter(
          (column) => column.field !== "total_controls"
        );
        return [
          filteredColumns[0], // Keep the first column (tenant)
          TotalCompliantColumn, // Add TotalCompliantColumn as second
          ...filteredColumns.slice(1), // Add the rest of the columns
        ];
      }
      if (activeSeverity === ControlSeverityMssp.NOT_DEPLOYED) {
        return filteredColumns.filter((column) => column.field !== "compliant");
      }
    }
    return columns;
  }, [activeSeverity]);

  return (
    <StyledDataGrid
      rows={rows}
      columns={filterColumns}
      autoHeight
      getRowId={(row) => row.tenant_id + row.tenant_name}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      loading={isLoadingTenantsData}
      boxSXProps={{
        border: "none",
        width: "100%",
        padding: 0,
        ".MuiDataGrid-root": {
          border: "none",
          boxShadow: "none",
        },
        ".MuiDataGrid-main": {},
      }}
    />
  );
};

export default StackCoverageTable;
